/**
 * Commerce API Client
 * 
 * This is the main API client for all commerce-service calls on the website.
 * 
 * In K8s deployment, calls go through local Next.js API routes which proxy
 * to backend services via internal cluster DNS.
 * 
 * For development/local testing, can optionally call external APIs directly.
 */
import { createCommerceClient, type CommerceClient } from '@erunna/api-client'
import type { GeoCoordinate, TransportMode } from '@erunna/api-client/types';
import { z } from 'zod';
import { PricingQuoteRequestSchema } from '@erunna/contracts-schema';
import { GeoPoint, DirectionsResponse, PricingEstimateResponse, PricingTransportResponse } from './schemas';
import { getServiceUrl } from '@/config/services';

type GeoPointType = z.infer<typeof GeoPoint>;

// Order-create payloads. The canonical scalar types (GeoCoordinate, TransportMode)
// are reused from the @erunna/api-client/types barrel; the payload SHAPES are
// declared here (the api-client's deep CreateErrandRequest/CreateOrderRequest
// named exports don't resolve cleanly under bundler resolution, so we keep the
// dependency to the stable scalar surface and the route handler owns the wire
// contract). Errand now, marketplace order as the parallel sibling (M1d).
export type JobLocation = GeoCoordinate & { address?: string };

/** Errand-create payload — mirrors proximity-matching POST /api/errands. */
export interface CreateErrandPayload {
  pickup: JobLocation;
  dropoff: JobLocation;
  kind?: 'errand' | 'parcel';
  description?: string;
  transportMode?: TransportMode;
  /** The backend-priced estimate the calculator produced (ZAR). Display-only. */
  estimatedPrice?: number;
  /**
   * The displayed fee expressing customer consent. Fixed create obtains its own
   * commerce quote and rejects a changed expected fee before creating a payable errand.
   */
  paymentAmount?: number;
  payment?: { amount: number; currency: string };
}

/** Marketplace order-create payload — mirrors commerce POST /api/orders (M1d). */
export interface CreateOrderPayload {
  deliveryAddress: JobLocation;
  vendorId?: string;
  items?: Array<{ productId: string; quantity: number; notes?: string }>;
  paymentMethod?: string;
  notes?: string;
}

/** Result of a successful create: a job id to route the customer to /tracking. */
export interface CreatedJob {
  jobId: string;
  kind: 'errand' | 'order';
  /**
   * The SERVER-AUTHORITATIVE total the customer will be charged (rands), read back
   * from the created entity — order.total = subtotal + deliveryFee + serviceFee −
   * discount (ADR 0039). The checkout must display THIS, not the client-side cart
   * subtotal, or the money screen shows a smaller number than the charge. Absent if
   * the create response omits it (checkout falls back to its passed amount).
   */
  total?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payment leg types (ADR 0048 R1) — charge-before-dispatch over saved cards.
// SAQ-A: the PAN never touches our code; hosted-page handoff for card entry, and
// the saved-card token is stored/used server-side only (stripped from listCards).
// ─────────────────────────────────────────────────────────────────────────────

/** A saved card as returned by GET /api/payment/cards/:userId (token stripped). */
export interface SavedCard {
  id: string;
  last4: string;
  brand: string;
  bank?: string;
  isDefault?: boolean;
}

/**
 * Result of POST /api/payment/charge_saved. `status` drives the FSM:
 * - success  → charged (resolve; route to track)
 * - send_pin → inline PIN step-up required
 * - send_otp → inline OTP step-up required
 * - pending  → NOT paid yet (fail-loud; never treat as success)
 * - failed   → declined (fail-loud)
 */
export interface ChargeResult {
  success: boolean;
  reference: string;
  status: 'success' | 'send_pin' | 'send_otp' | 'pending' | 'failed';
  message?: string;
}

/**
 * Result of a step-up submit (submit_pin → success|requires_otp|failed;
 * submit_otp → success|failed). `nextAction` is 'submit_otp' after a PIN that
 * still needs an OTP. Never resolve success unless `status === 'success'`.
 */
export interface StepUpResult {
  status: 'success' | 'requires_otp' | 'failed';
  reference: string;
  nextAction?: 'submit_otp';
}

/** Result of POST /api/payment/init — the hosted-page handoff (no transaction persisted). */
export interface InitResult {
  authorization_url: string;
  access_code: string;
  reference: string;
  publicKey: string;
  type: string;
}

/** Result of POST /api/payment/add-card — the hosted-page handoff for new card entry. */
export interface AddCardResult {
  authorization_url: string;
  reference: string;
  publicKey: string;
}

/**
 * The payment TARGET identity (ADR 0056 A1 — order-scoped marketplace pay leg).
 * Exactly one of these routes the charge on the backend: an `errandId` charges a
 * genuine deliveryType='errand' (open-errand / parcel fulfilment); an `orderId`
 * charges a marketplace ORDER (the server derives the authoritative amount from
 * order.total, ADR 0039). The linked fulfilment 'errand' is NEVER the payment
 * handle for an order — passing the orderId in errandId 403s (target not found).
 * The backend's InitRequestSchema/ChargeSavedSchema require one of errandId /
 * orderId / parcelId, so these are additive-optional, not a breaking change.
 */
export interface PaymentInitPayload {
  errandId?: string;
  orderId?: string;
  email: string;
  amountCents: number;
  currency?: 'ZAR';
}

export interface ChargeSavedPayload {
  errandId?: string;
  orderId?: string;
  amountCents: number;
  email?: string;
  cardId?: string;
}

/**
 * Extract the created job id from a backend create response.
 *
 * CUST-08: NEVER fabricate an id. A "successful" create with no id is a broken
 * contract — throw so the caller does not route to a fake tracking page. The
 * proximity/commerce create routes return { success, data: { errandId|orderId,
 * errand|order } } (or a top-level id on the entity); a failure throws earlier
 * (non-2xx) before we get here.
 */
function extractJobId(raw: unknown, kind: 'errand' | 'order'): CreatedJob {
  const body = (raw ?? {}) as Record<string, any>;
  if (body.success === false) {
    const err = new Error(String(body.error || body.message || 'create_failed'));
    (err as any).code = body.code;
    throw err;
  }
  const data = (body.data ?? body) as Record<string, any>;
  const entity = data[kind] ?? {};
  const jobId =
    data[`${kind}Id`] ?? data.id ?? entity.id ?? body[`${kind}Id`] ?? body.id;

  if (typeof jobId !== 'string' || jobId.length === 0) {
    throw new Error('create_succeeded_without_job_id');
  }
  // Server-authoritative total (rands) off the created entity, for the money screen.
  // The charge itself is derived server-side from order.total regardless (ADR 0039);
  // this only aligns what the customer SEES with what they are billed.
  const rawTotal = data.total ?? entity.total ?? body.total;
  const total = typeof rawTotal === 'number' && Number.isFinite(rawTotal) && rawTotal > 0
    ? rawTotal
    : undefined;
  return { jobId, kind, total };
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

// Check if we should use local API routes (default in K8s) or external APIs (for local dev)
let useLocalRoutes = true;
let forceExternal = false;
// Lazy — getServiceUrl uses process.env bracket notation which fails client-side
let _externalApi: string | null = null;
function getExternalApi(): string {
  if (!_externalApi) {
    try { _externalApi = getServiceUrl('commerce'); } catch { _externalApi = ''; }
  }
  return _externalApi;
}
const LOCAL_DEV_API = 'http://127.0.0.1:8090';

try {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    // Use external APIs if explicitly requested via ?api=external or ?api=local (for local dev server)
    forceExternal = url.searchParams.get('api') === 'external';
    const forceLocalDev = url.searchParams.get('api') === 'local' || localStorage.getItem('ERUNNA_FORCE_LOCAL_APIS') === '1';
    if (forceExternal || forceLocalDev) {
      useLocalRoutes = false;
    }
  }
} catch { /* URL parsing may fail */ }

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get correlation ID for request tracing
 */
function getCorrelationId(): string | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null;
  }
  const KEY = 'ERUNNA_CORR_ID';
  let correlationId = localStorage.getItem(KEY);
  if (!correlationId) {
    correlationId = 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, correlationId);
  }
  return correlationId;
}

/**
 * Make a fetch request with standard headers
 */
async function fetchWithHeaders(url: string, options: RequestInit = {}): Promise<Response> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-App-Platform': 'web',
    ...(options.headers || {}),
  };

  const correlationId = getCorrelationId();
  if (correlationId) {
    (headers as Record<string, string>)['X-Correlation-Id'] = correlationId;
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Best-effort Firebase ID token from the global firebase compat shim.
 * The bridge exposes window.firebase.auth().currentUser (same source the
 * direct-client interceptors use). Returns null when signed out.
 */
async function maybeGetIdToken(): Promise<string | null> {
  try {
    const w = typeof window !== 'undefined'
      ? (window as unknown as { firebase?: { auth?: () => { currentUser?: { getIdToken: () => Promise<string> } } } })
      : null;
    const user = w?.firebase?.auth?.()?.currentUser;
    if (user) return await user.getIdToken();
  } catch { /* auth optional */ }
  return null;
}

/**
 * Like fetchWithHeaders but attaches the Firebase Bearer token. Used by
 * owner-bound writes (errand/order create) so the Next route handler can
 * forward identity to the backend, which binds the owner from the principal.
 *
 * `explicitToken` lets the caller pass a token from the MODULAR Firebase SDK
 * (the AuthContext session — including a silent anonymous guest). This matters:
 * the modular AuthContext and the legacy `window.firebase` compat shim are
 * SEPARATE auth sessions, so the anon guest only exists on the modular side. The
 * compat-shim fallback (`maybeGetIdToken`) is kept for the legacy bridge surfaces
 * that have no modular token, but owner-bound creates from the React app must pass
 * the modular token so a guest's request is actually authenticated (not a 401).
 */
async function fetchWithAuthHeaders(
  url: string,
  options: RequestInit = {},
  explicitToken?: string | null,
): Promise<Response> {
  const token = explicitToken ?? (await maybeGetIdToken());
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetchWithHeaders(url, { ...options, headers });
}

// ─────────────────────────────────────────────────────────────────────────────
// Commerce Client (for direct backend access - used in local dev)
// ─────────────────────────────────────────────────────────────────────────────

let _commerceClient: CommerceClient | null = null;

/**
 * Get or create the commerce client singleton.
 * Used when bypassing local API routes (for local development).
 */
export function getCommerceClient(): CommerceClient {
  if (!_commerceClient) {
    const baseURL = forceExternal ? getExternalApi() : LOCAL_DEV_API;
    _commerceClient = createCommerceClient({ baseURL });

    _commerceClient.addInterceptor({
      request: async (config) => {
        try {
          config.headers = config.headers || {};
          
          const correlationId = getCorrelationId();
          if (correlationId) {
            config.headers['X-Correlation-Id'] = correlationId;
          }
          config.headers['X-App-Platform'] = 'web';

          // Get Firebase auth token if available
          const w = typeof window !== 'undefined' ? (window as unknown as { firebase?: { auth?: () => { currentUser?: { getIdToken: () => Promise<string> } } } }) : null;
          const user = w?.firebase?.auth?.()?.currentUser;
          if (user) {
            const token = await user.getIdToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch {
          // Silently fail - auth is optional for public endpoints
        }
        return config;
      }
    });
  }

  return _commerceClient;
}


// ─────────────────────────────────────────────────────────────────────────────
// Direct-backend errand create (local dev only — ?api=external|local)
//
// In K8s/prod the browser hits the Next route handler, so this path is the
// dev-only fallback. Errand-create lives on proximity-matching; we POST to it
// with a thin typed client (createCommerceClient gives us the typed transport +
// the same Firebase-token interceptor). We use the generic .post so we don't
// depend on the api-client's deep ProximityClient/createProximityClient exports.
// ─────────────────────────────────────────────────────────────────────────────

let _proximityClient: CommerceClient | null = null;

function getExternalProximityApi(): string {
  try { return getServiceUrl('proximity'); } catch { return ''; }
}

/** Get or create the direct proximity transport (errand create), local-dev only. */
function getProximityClient(): CommerceClient {
  if (!_proximityClient) {
    const baseURL = forceExternal ? getExternalProximityApi() : LOCAL_DEV_API;
    _proximityClient = createCommerceClient({ baseURL });

    _proximityClient.addInterceptor({
      request: async (config) => {
        try {
          config.headers = config.headers || {};

          const correlationId = getCorrelationId();
          if (correlationId) {
            config.headers['X-Correlation-Id'] = correlationId;
          }
          config.headers['X-App-Platform'] = 'web';

          // Errand create is owner-bound — attach the Firebase token if present.
          const w = typeof window !== 'undefined' ? (window as unknown as { firebase?: { auth?: () => { currentUser?: { getIdToken: () => Promise<string> } } } }) : null;
          const user = w?.firebase?.auth?.()?.currentUser;
          if (user) {
            const token = await user.getIdToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch {
          // Silently fail - auth is optional for public endpoints
        }
        return config;
      }
    });
  }

  return _proximityClient;
}

/**
 * Parse an envelope response, throwing the @erunna error-contract on !ok (same
 * pattern as errands.create): an Error carrying `.status` and `.code`, never a
 * masked success. Returns the parsed `data` payload on success.
 */
async function parseEnvelope<T>(response: Response, fallbackMessage: string): Promise<T> {
  const raw = await response.json().catch(() => ({} as Record<string, any>));
  if (!response.ok || (raw && raw.success === false)) {
    const err = new Error((raw && (raw.error || raw.message)) || fallbackMessage);
    (err as any).status = response.status;
    (err as any).code = raw && raw.code;
    throw err;
  }
  return (raw && (raw.data ?? raw)) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// API Object (uses local routes in K8s, external APIs in local dev)
// ─────────────────────────────────────────────────────────────────────────────

export const Api = {
  maps: {
    /**
     * Search for places by text query
     */
    search: async (searchText: string, locationBias?: { latitude: number; longitude: number }) => {
      if (useLocalRoutes) {
        const response = await fetchWithHeaders('/api/maps/search', {
          method: 'POST',
          body: JSON.stringify({ searchText, locationBias }),
        });
        return response.json();
      }
      // Fallback to direct client for local dev
      const client = getCommerceClient();
      // MapsApi.searchPlaces takes (searchText, locationBias?) — positional, NOT a
      // params object. The deleted ambient shim declared an object parameter, so
      // this call used to send `{query, location}` as the search TEXT.
      return client.maps.searchPlaces(searchText, locationBias ?? null);
    },

    /**
     * Get details for a specific place
     */
    details: async (placeId: string) => {
      if (useLocalRoutes) {
        const response = await fetchWithHeaders('/api/maps/details', {
          method: 'POST',
          body: JSON.stringify({ placeId }),
        });
        return response.json();
      }
      // Fallback to direct client for local dev
      const client = getCommerceClient();
      // Positional placeId, same shim-hidden signature error as searchPlaces above.
      return client.maps.getPlaceDetails(placeId);
    },

    /**
     * Get directions between two points
     */
    directions: async (origin: GeoPointType, destination: GeoPointType, signal?: AbortSignal) => {
      signal?.throwIfAborted();
      if (useLocalRoutes) {
        const response = await fetchWithHeaders('/api/maps/directions', {
          method: 'POST',
          body: JSON.stringify({ origin, destination }),
          signal,
        });
        const raw = await response.json();
        return DirectionsResponse.parse(raw);
      }
      // Fallback to direct client for local dev
      const client = getCommerceClient();
      const raw = await client.maps.getRoute(origin, destination);
      signal?.throwIfAborted();
      return DirectionsResponse.parse(raw);
    },
  },

  pricing: {
    /**
     * Get pricing for a specific transport mode.
     *
     * `transportMode` is the CLOSED @erunna/api-client union
     * ('foot'|'bicycle'|'scooter'|'car'|'bakkie'), not a free string — the
     * deleted ambient shim typed it `string`, which let any value through to a
     * backend that rejects it. Kept narrow deliberately: an unknown mode must
     * fail loudly, never be coerced to a default.
     */
    transport: async (transportMode: TransportMode, distanceKm: number) => {
      if (useLocalRoutes) {
        const response = await fetchWithHeaders('/api/pricing/transport', {
          method: 'POST',
          body: JSON.stringify({ transportMode, distanceKm }),
        });
        const raw = await response.json();
        return PricingTransportResponse.parse(raw);
      }
      // Fallback to direct client for local dev
      const client = getCommerceClient();
      const raw = await client.pricing.calculateAuctionTransport({ transportMode, distanceKm });
      return PricingTransportResponse.parse(raw);
    },

    /**
     * Get available transport modes for a distance
     */
    transportModes: async (distanceKm: number) => {
      if (useLocalRoutes) {
        const response = await fetchWithHeaders(`/api/pricing/transport-modes?distanceKm=${distanceKm}`, {
          method: 'GET',
        });
        return response.json();
      }
      // Fallback to direct client for local dev
      const client = getCommerceClient();
      return client.pricing.getAvailableTransportModes(distanceKm);
    },

    /**
     * Get pricing estimate for a trip.
     *
     * Both branches now hit the SAME backend endpoint with the SAME body: the
     * Next proxy forwards to commerce `POST /api/v1/pricing/quote`, and the
     * direct-client fallback calls `pricing.quote()`, which posts to
     * `/v1/pricing/quote`. The fallback previously called `pricing.getEstimate`
     * — a method that does not exist in @erunna/api-client at all (0 occurrences
     * in the published package) and only compiled because the ambient shim
     * invented it. At runtime it threw `getEstimate is not a function`, so the
     * ?api=external|local dev path was fully broken.
     */
    estimate: async (pickup: GeoPointType, dropoff: GeoPointType, serviceType: string, transportMode?: string | null, distanceKm?: number, signal?: AbortSignal) => {
      signal?.throwIfAborted();
      const body = PricingQuoteRequestSchema.parse({
        pickup, dropoff, serviceType,
        transportMode: transportMode || undefined,
        distanceKm,
      });

      const raw = useLocalRoutes
        ? await (await fetchWithHeaders('/api/pricing/estimate', {
            method: 'POST',
            body: JSON.stringify(body),
            signal,
          })).json()
        : await getCommerceClient().pricing.quote(body, signal ? { signal } : undefined);

      signal?.throwIfAborted();

      // The canonical v1 endpoint returns a bare quote on both proxy and SDK paths.
      return PricingEstimateResponse.parse(raw);
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Order-create spine — generic over job kind (errand now, marketplace next).
  //
  // Same dual-mode shape as maps/pricing: in K8s the browser hits this app's own
  // Next route handler (/api/errands, /api/orders), which forwards the caller's
  // Firebase Bearer token to the owning backend (proximity for errands, commerce
  // for orders); the backend binds the owner from the principal. For local dev
  // (?api=external|local) it falls back to the typed client directly. The two
  // namespaces share one shape so marketplace (M1d) is a sibling, not a fork.
  // ───────────────────────────────────────────────────────────────────────────
  errands: {
    /**
     * Create a real errand from the calculator's priced estimate. CUST-08: throws
     * on missing id. `opts.idToken` carries the MODULAR AuthContext token (the
     * guest/anon session) so the create is actually authenticated; `opts.idempotencyKey`
     * is a stable per-attempt key forwarded as Idempotency-Key so a double-tap/retry
     * does not create two errands (standard #3).
     */
    create: async (
      payload: CreateErrandPayload,
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<CreatedJob> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      if (useLocalRoutes) {
        const response = await fetchWithAuthHeaders(
          '/api/errands',
          {
            method: 'POST',
            headers: idemHeader,
            body: JSON.stringify(payload),
          },
          opts?.idToken,
        );
        const raw = await response.json().catch(() => ({}));
        if (!response.ok) {
          const err = new Error((raw && (raw.error || raw.message)) || 'errand_create_failed');
          (err as any).status = response.status;
          (err as any).code = raw && raw.code;
          throw err;
        }
        return extractJobId(raw, 'errand');
      }
      // Fallback to direct backend for local dev
      const client = getProximityClient();
      const raw = await client.post<unknown>('/api/errands', payload);
      return extractJobId(raw, 'errand');
    },
  },

  orders: {
    // Marketplace sibling (M1d). Wired + type-ready; the catalogue/cart UI that
    // fills CreateOrderPayload lands with that slice — this is additive, not a rewrite.
    /** Create a marketplace order. CUST-08: throws on missing id. Same auth/idempotency contract as errands.create. */
    create: async (
      payload: CreateOrderPayload,
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<CreatedJob> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      if (useLocalRoutes) {
        const response = await fetchWithAuthHeaders(
          '/api/orders',
          {
            method: 'POST',
            headers: idemHeader,
            body: JSON.stringify(payload),
          },
          opts?.idToken,
        );
        const raw = await response.json().catch(() => ({}));
        if (!response.ok) {
          const err = new Error((raw && (raw.error || raw.message)) || 'order_create_failed');
          (err as any).status = response.status;
          (err as any).code = raw && raw.code;
          throw err;
        }
        return extractJobId(raw, 'order');
      }
      // Fallback to direct backend for local dev
      const client = getCommerceClient();
      const raw = await client.post<unknown>('/api/orders', payload);
      return extractJobId(raw, 'order');
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Payment leg (ADR 0048 R1) — charge-before-dispatch over saved cards.
  //
  // Unlike maps/pricing/errands, payments route ONLY through this app's Next
  // proxy (/api/payment/*) — there is no direct-backend dev path: the money leg
  // is owner-bound and the proxy is the single chokepoint that forwards identity
  // and idempotency to commerce-service. Authed calls use fetchWithAuthHeaders
  // with an explicit modular token (opts.idToken), exactly like errands.create;
  // the public verify callback uses fetchWithHeaders. Every method passes the
  // backend status straight through (parseEnvelope throws on !ok) — a failed or
  // pending charge is NEVER coerced into success (standard #2 fail-loud).
  // ───────────────────────────────────────────────────────────────────────────
  payments: {
    /** Hosted-page handoff for a customer with NO saved card. Does NOT persist a transaction. */
    init: async (
      payload: PaymentInitPayload,
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<InitResult> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      const response = await fetchWithAuthHeaders(
        '/api/payment/init',
        { method: 'POST', headers: idemHeader, body: JSON.stringify(payload) },
        opts?.idToken,
      );
      return parseEnvelope<InitResult>(response, 'payment_init_failed');
    },

    /**
     * Charge the customer's saved card for an existing target — an errand
     * (errandId) or a marketplace order (orderId). The backend charges the
     * SERVER-AUTHORITATIVE amount regardless of amountCents: errand.paymentAmount
     * for an errand, order.total for an order (VULN-008/ADR 0039). Returns the
     * charge FSM status (success|send_pin|send_otp|pending|failed) — the caller
     * fails loud on anything but `success`.
     */
    chargeSaved: async (
      payload: ChargeSavedPayload,
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<ChargeResult> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      const response = await fetchWithAuthHeaders(
        '/api/payment/charge_saved',
        { method: 'POST', headers: idemHeader, body: JSON.stringify(payload) },
        opts?.idToken,
      );
      return parseEnvelope<ChargeResult>(response, 'charge_failed');
    },

    /** Inline PIN step-up. status: success | requires_otp | failed. */
    submitPin: async (
      payload: { reference: string; pin: string },
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<StepUpResult> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      const response = await fetchWithAuthHeaders(
        '/api/payment/charge_saved/submit_pin',
        { method: 'POST', headers: idemHeader, body: JSON.stringify(payload) },
        opts?.idToken,
      );
      return parseEnvelope<StepUpResult>(response, 'pin_failed');
    },

    /** Inline OTP step-up. status: success | failed. */
    submitOtp: async (
      payload: { reference: string; otp: string },
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<StepUpResult> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      const response = await fetchWithAuthHeaders(
        '/api/payment/charge_saved/submit_otp',
        { method: 'POST', headers: idemHeader, body: JSON.stringify(payload) },
        opts?.idToken,
      );
      return parseEnvelope<StepUpResult>(response, 'otp_failed');
    },

    /** Saved cards for a user (own/admin enforced backend-side). Token stripped. */
    listCards: async (
      userId: string,
      opts?: { idToken?: string | null },
    ): Promise<SavedCard[]> => {
      const response = await fetchWithAuthHeaders(
        `/api/payment/cards/${encodeURIComponent(userId)}`,
        { method: 'GET' },
        opts?.idToken,
      );
      const data = await parseEnvelope<SavedCard[] | undefined>(response, 'list_cards_failed');
      return Array.isArray(data) ? data : [];
    },

    /** Hosted-page handoff to add a new card (off-site secure entry, SAQ-A). */
    addCard: async (
      payload: { email: string },
      opts?: { idToken?: string | null; idempotencyKey?: string },
    ): Promise<AddCardResult> => {
      const idemHeader: Record<string, string> = opts?.idempotencyKey
        ? { 'Idempotency-Key': opts.idempotencyKey }
        : {};
      const response = await fetchWithAuthHeaders(
        '/api/payment/add-card',
        { method: 'POST', headers: idemHeader, body: JSON.stringify(payload) },
        opts?.idToken,
      );
      return parseEnvelope<AddCardResult>(response, 'add_card_failed');
    },

    /** Public verify callback (no auth) — the source of truth for a hosted-page return. */
    verify: async (reference: string): Promise<unknown> => {
      const response = await fetchWithHeaders(
        `/api/payment/verify/${encodeURIComponent(reference)}`,
        { method: 'GET' },
      );
      return parseEnvelope<unknown>(response, 'verify_failed');
    },
  },
};

export default Api;
